/*
 * Infinite scroll for the collection product grid.
 *
 * Markup (rendered inside #ProductGridContainer so it survives facet/sort
 * re-renders) is a custom element carrying the URL of the next page:
 *
 *   <infinite-scroll data-next-url="..." data-grid="product-grid">
 *     <div class="infinite-scroll__sentinel"></div>
 *     ... loading spinner ...
 *   </infinite-scroll>
 *
 * When we get near the bottom of the list we fetch the next page, lift its
 * product cards into the current grid, and advance data-next-url. The
 * element re-initialises automatically whenever FacetFiltersForm replaces
 * the grid container (connectedCallback fires again on the new node).
 *
 * Triggering uses three mechanisms so it stays reliable across browsers and
 * layouts: an IntersectionObserver on the sentinel, a throttled scroll/resize
 * listener as a fallback, and an initial check in case the list is shorter
 * than the viewport.
 */
class InfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.loading = false;
    this.sentinel = this.querySelector('.infinite-scroll__sentinel');
    this.spinner = this.querySelector('.infinite-scroll__spinner');
    this.threshold = 600; // px from viewport bottom at which we start loading

    this.onScroll = this.onScroll.bind(this);

    if ('IntersectionObserver' in window && this.sentinel) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
        },
        { rootMargin: `${this.threshold}px 0px` }
      );
      this.observer.observe(this.sentinel);
    }

    // Fallback / belt-and-braces: also react to scrolling and resizing. The
    // loading guard keeps these from racing the observer.
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    // The grid may be shorter than the viewport (nothing to scroll), so check
    // once after layout settles.
    requestAnimationFrame(() => this.maybeLoad());
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  onScroll() {
    if (this.scrollScheduled) return;
    this.scrollScheduled = true;
    requestAnimationFrame(() => {
      this.scrollScheduled = false;
      this.maybeLoad();
    });
  }

  get nextUrl() {
    return this.dataset.nextUrl;
  }

  maybeLoad() {
    if (this.loading || !this.nextUrl) return;
    const anchor = this.sentinel || this;
    const rect = anchor.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top <= viewportHeight + this.threshold) this.loadNext();
  }

  async loadNext() {
    if (this.loading || !this.nextUrl) return;
    this.loading = true;
    if (this.spinner) this.spinner.hidden = false;

    try {
      const response = await fetch(this.nextUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');

      const gridId = this.dataset.grid || 'product-grid';
      const grid = document.getElementById(gridId);
      const newGrid = doc.getElementById(gridId);
      if (grid && newGrid) {
        const fragment = document.createDocumentFragment();
        newGrid.querySelectorAll(':scope > li.grid__item').forEach((item) => {
          fragment.appendChild(item);
        });
        grid.appendChild(fragment);
      }

      // Advance to the page after the one we just loaded.
      const nextTrigger = doc.querySelector('infinite-scroll');
      this.dataset.nextUrl = nextTrigger ? nextTrigger.dataset.nextUrl || '' : '';

      if (!this.nextUrl) {
        if (this.observer) this.observer.disconnect();
        this.remove();
      }
    } catch (error) {
      console.error('Infinite scroll failed:', error);
      // Stop auto-loading so we don't hammer a failing endpoint.
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    } finally {
      this.loading = false;
      if (this.spinner) this.spinner.hidden = true;
      // After appending, the page may still be short enough to need more.
      if (this.isConnected) requestAnimationFrame(() => this.maybeLoad());
    }
  }
}

if (!customElements.get('infinite-scroll')) {
  customElements.define('infinite-scroll', InfiniteScroll);
}
